/**
 * QR Code Widget - Displays a QR code with custom dot and corner styles.
 * Uses qrcode core library for matrix generation, renders as SVG.
 */

import type { Widget, WidgetConfig } from './index';

export type QRDotStyle = 'squares' | 'dots' | 'rounded';
export type QRCornerStyle = 'square' | 'rounded' | 'dot';
export type QRCornerDotStyle = 'square' | 'dot';

export interface QRConfig extends WidgetConfig {
  content?: string;
  size?: number;
  darkColor?: string;
  lightColor?: string;
  foreground?: string;
  background?: string;
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  label?: string;
  labelPosition?: 'top' | 'bottom';
  labelFontSize?: number;
  labelColor?: string;
  dotStyle?: QRDotStyle;
  cornerStyle?: QRCornerStyle;
  cornerDotStyle?: QRCornerDotStyle;
  cornerColor?: string;
}

function isFinderRegion(row: number, col: number, size: number): boolean {
  if (row < 7 && col < 7) return true;
  if (row < 7 && col >= size - 7) return true;
  if (row >= size - 7 && col < 7) return true;
  return false;
}

function buildFinderSVG(
  oRow: number, oCol: number,
  cornerStyle: QRCornerStyle, cornerDotStyle: QRCornerDotStyle,
  cornerColor: string, bgColor: string,
): string {
  let svg = '';
  // Outer 7x7
  if (cornerStyle === 'dot') {
    svg += `<circle cx="${oCol + 3.5}" cy="${oRow + 3.5}" r="3.5" fill="${cornerColor}"/>`;
    svg += `<circle cx="${oCol + 3.5}" cy="${oRow + 3.5}" r="2.5" fill="${bgColor}"/>`;
  } else {
    const outerRx = cornerStyle === 'rounded' ? 1.4 : 0;
    const innerRx = cornerStyle === 'rounded' ? 0.8 : 0;
    svg += `<rect x="${oCol}" y="${oRow}" width="7" height="7" rx="${outerRx}" fill="${cornerColor}"/>`;
    svg += `<rect x="${oCol + 1}" y="${oRow + 1}" width="5" height="5" rx="${innerRx}" fill="${bgColor}"/>`;
  }
  // Center dot 3x3
  if (cornerDotStyle === 'dot') {
    svg += `<circle cx="${oCol + 3.5}" cy="${oRow + 3.5}" r="1.5" fill="${cornerColor}"/>`;
  } else {
    const dotRx = cornerStyle === 'rounded' ? 0.4 : 0;
    svg += `<rect x="${oCol + 2}" y="${oRow + 2}" width="3" height="3" rx="${dotRx}" fill="${cornerColor}"/>`;
  }
  return svg;
}

function buildDataModuleSVG(
  row: number, col: number, dotStyle: QRDotStyle, fgColor: string,
): string {
  switch (dotStyle) {
    case 'dots':
      return `<circle cx="${col + 0.5}" cy="${row + 0.5}" r="0.4" fill="${fgColor}"/>`;
    case 'rounded':
      return `<rect x="${col + 0.05}" y="${row + 0.05}" width="0.9" height="0.9" rx="0.3" fill="${fgColor}"/>`;
    default:
      return `<rect x="${col}" y="${row}" width="1" height="1" fill="${fgColor}"/>`;
  }
}

export class QRCodeWidget implements Widget {
  private container: HTMLElement;
  private config: QRConfig;

  constructor(config: QRConfig = {}) {
    this.config = {
      content: 'https://govista.app',
      size: 200,
      darkColor: '#000000',
      lightColor: '#ffffff',
      errorCorrectionLevel: 'M',
      labelPosition: 'bottom',
      labelFontSize: 14,
      labelColor: '#ffffff',
      dotStyle: 'squares',
      cornerStyle: 'square',
      cornerDotStyle: 'square',
      ...config,
    };

    this.container = document.createElement('div');
    this.container.className = 'widget-qr';
  }

  render(): HTMLElement {
    this.container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    `;

    this.generateQR();

    return this.container;
  }

  update(): void {
    this.generateQR();
  }

  private async generateQR(): Promise<void> {
    try {
      const QRCodeCore = await this.loadQRCodeCore();
      if (QRCodeCore) {
        this.renderCustomSVG(QRCodeCore);
      } else {
        this.renderFallback();
      }
    } catch (error) {
      console.error('[QRCodeWidget] Failed to generate QR:', error);
      this.renderFallback();
    }
  }

  private async loadQRCodeCore(): Promise<any> {
    try {
      // @ts-expect-error — no type declarations for core module
      const mod = await import('qrcode/lib/core/qrcode');
      return mod.default || mod;
    } catch {
      try {
        // Fallback: try the full qrcode library for matrix
        const QRCode = await import('qrcode');
        return { create: (text: string, opts: any) => (QRCode as any).create(text, opts) };
      } catch {
        return null;
      }
    }
  }

  private renderCustomSVG(QRCodeCore: any): void {
    const fgColor = this.config.foreground || this.config.darkColor || '#000000';
    const bgColor = this.config.background || this.config.lightColor || '#ffffff';
    const cornerColor = this.config.cornerColor || fgColor;
    const dotStyle = this.config.dotStyle || 'squares';
    const cornerStyle = this.config.cornerStyle || 'square';
    const cornerDotStyle = this.config.cornerDotStyle || 'square';

    let qr: any;
    try {
      qr = QRCodeCore.create(this.config.content || 'https://govista.app', {
        errorCorrectionLevel: this.config.errorCorrectionLevel || 'M',
      });
    } catch {
      this.renderFallback();
      return;
    }

    const data: Uint8Array = qr.modules.data;
    const moduleSize: number = qr.modules.size;

    // Build SVG string
    let svgContent = `<rect fill="${bgColor}" width="${moduleSize}" height="${moduleSize}"/>`;

    // Finder patterns
    const finderPositions = [
      { row: 0, col: 0 },
      { row: 0, col: moduleSize - 7 },
      { row: moduleSize - 7, col: 0 },
    ];
    for (const pos of finderPositions) {
      svgContent += buildFinderSVG(pos.row, pos.col, cornerStyle, cornerDotStyle, cornerColor, bgColor);
    }

    // Data modules
    for (let row = 0; row < moduleSize; row++) {
      for (let col = 0; col < moduleSize; col++) {
        if (!data[row * moduleSize + col]) continue;
        if (isFinderRegion(row, col, moduleSize)) continue;
        svgContent += buildDataModuleSVG(row, col, dotStyle, fgColor);
      }
    }

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${moduleSize} ${moduleSize}" shape-rendering="crispEdges" style="width:100%;height:100%;">${svgContent}</svg>`;

    let html = '';
    if (this.config.label && this.config.labelPosition === 'top') {
      html += `<div style="font-size:${this.config.labelFontSize}px;color:${this.config.labelColor};margin-bottom:12px;text-align:center;">${this.config.label}</div>`;
    }

    html += `<div style="background:${bgColor};padding:16px;border-radius:12px;display:flex;align-items:center;justify-content:center;max-width:100%;max-height:100%;">${svg}</div>`;

    if (this.config.label && this.config.labelPosition === 'bottom') {
      html += `<div style="font-size:${this.config.labelFontSize}px;color:${this.config.labelColor};margin-top:12px;text-align:center;">${this.config.label}</div>`;
    }

    this.container.innerHTML = html;
  }

  private renderFallback(): void {
    let html = '';
    if (this.config.label && this.config.labelPosition === 'top') {
      html += `<div style="font-size:${this.config.labelFontSize}px;color:${this.config.labelColor};margin-bottom:12px;text-align:center;">${this.config.label}</div>`;
    }
    html += `
      <div style="width:${this.config.size}px;height:${this.config.size}px;background:${this.config.lightColor};border-radius:12px;display:flex;align-items:center;justify-content:center;flex-direction:column;padding:16px;box-sizing:border-box;">
        <div style="font-size:12px;color:${this.config.darkColor};text-align:center;word-break:break-all;">${this.config.content}</div>
      </div>
    `;
    if (this.config.label && this.config.labelPosition === 'bottom') {
      html += `<div style="font-size:${this.config.labelFontSize}px;color:${this.config.labelColor};margin-top:12px;text-align:center;">${this.config.label}</div>`;
    }
    this.container.innerHTML = html;
  }

  destroy(): void {
    // no-op
  }
}
